/**
 * DocumentItemsSection.tsx — Dokumenty: linie dokumentu (increment 1).
 *
 * Read-only widok pozycji dokumentu — tabela linii (`document_items`) +
 * podsumowanie VAT per stawka (`document_vat_entries`) + wiersz sum
 * nagłówkowych (`amountNet`/`amountVat`/`amountGross` — wartości WALUTOWE
 * dokumentu, w `currencyCode` dokumentu, NIE zawsze PLN; kontrakt API nie daje
 * wariantu `...InCurrency` dla kwot nagłówkowych bo nie ma czego przeliczać —
 * to JUŻ jest wartość w walucie dokumentu, w przeciwieństwie do linii/
 * rozbicia VAT, które dodatkowo niosą bazowy (PLN) odpowiednik, patrz
 * `document-items.ts`).
 *
 * Wartości w kolumnach linii/rozbicia VAT pokazują KOMPLET WALUTOWY dokumentu
 * (`...InCurrency`) — dla dokumentu walutowego (`currencyCode !== 'PLN'`)
 * nagłówki kolumn dostają sufiks `(KOD_WALUTY)`, a pod wartością główną
 * pokazuje się mała szara linia z odpowiednikiem bazowym (PLN), żeby nie
 * gubić informacji o przeliczeniu bez zaśmiecania tabeli osobnymi kolumnami.
 *
 * Empty state (brak `items`) dotyczy WYŁĄCZNIE tabeli linii + rozbicia VAT —
 * dokumenty sprzed migracji 0094 (bez `document_items`) wciąż mają wypełnione
 * kwoty nagłówkowe, więc wiersz sum renderuje się niezależnie od tego czy
 * `items` jest pusty, dopóki którakolwiek z `amountNet`/`amountVat`/
 * `amountGross` jest non-null.
 *
 * Edycja (increment 2) — POZA zakresem tego komponentu, patrz
 * `docs/refactor/SHARED-EXTRACTION-BACKLOG.md` / plan fazy.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  effectiveColumnAlign,
  type ResolvedEmbeddedTableColumn,
  resolveEmbeddedTableColumns,
} from '@saas/shared';

import {
  type DocumentItemColumnId,
  type DocumentLineItem,
  type DocumentVatBreakdownRow,
  extColumnSlug,
  isExtColumnId,
} from '@/lib/documents/document-items';
import type { DocumentItemExtFieldDef } from '@/lib/documents/use-document-item-ext-fields';
import {
  columnAlignClass,
  fixedTableMinWidth,
  formatDecimalCell,
  formatDecimalCurrencyCell,
  hasExplicitWidths,
  isFormattableCurrencyCode,
  TableColgroup,
} from '@/lib/embedded-table/cells';
import {
  asDocumentItemColumnId,
  documentItemsLegacyReader,
  documentItemsReadViewDescriptors,
} from '@/lib/embedded-table/document-items-descriptors';
import { useDocumentItemColumnsModel } from '@/lib/embedded-table/use-document-item-columns-model';
import { cn } from '@/lib/utils';

export interface DocumentItemsSectionProps {
  items: DocumentLineItem[] | null | undefined;
  vatBreakdown: DocumentVatBreakdownRow[] | null | undefined;
  amountNet: string | null | undefined;
  amountVat: string | null | undefined;
  amountGross: string | null | undefined;
  currencyCode: string | null | undefined;
  /** Ustawienia elementu `document_items_view` z layoutu (`element.settings`): układ kolumn
   * (`tableColumns` + maska `hiddenColumnIds`), a gdy go brak — starszy `columnConfig`.
   * Brak ustawień → obsługiwany zestaw kolumn w kolejności edycji (ta sama, którą pokazuje edytor
   * układu w kreatorze). Kolumny, których ten widok nie
   * obsługuje (edytor-only `product`/`productCode`/`pkwiu`/`cn`/`gtin`), są ciche pomijane;
   * kolumny wymagane i zablokowane (Lp., nazwa, ilość, cena, VAT) są pokazane zawsze. */
  columnSettings?: Record<string, unknown>;
}

const EMPTY_DASH = '—';

/**
 * Formatuje decimal-string jako kwotę walutową (2 miejsca po przecinku; `decimals` z układu
 * kolumny — tylko prezentacja — zastępuje to 2, waluta zostaje). Kwoty to `numeric(14,2)`
 * (najwyżej 14 cyfr), więc konwersja przez `Number` jest tu dokładna — w odróżnieniu od ceny
 * jednostkowej (`formatUnitPrice`), która może mieć ponad 15 cyfr znaczących.
 */
function formatMoney(
  value: string | null | undefined,
  currency: string,
  decimals?: number,
): string {
  if (value == null || value === '') return EMPTY_DASH;
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  // `currencyCode` to pole tekstowe z bazy — kod spoza ISO 4217 rzuca z `Intl.NumberFormat` i bez
  // przechwycenia ubiłby render całej sekcji; pokazujemy wartość z surowym kodem (jak edytor).
  if (!isFormattableCurrencyCode(currency)) return `${value} ${currency}`;
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals ?? 2,
    maximumFractionDigits: decimals ?? 2,
  }).format(n);
}

/**
 * Formatuje decimal-string jako cenę jednostkową walutową (do 6 miejsc — precyzja unit_price_net;
 * `decimals` z układu kolumny ustala stałą liczbę miejsc, waluta zostaje).
 *
 * Cena jednostkowa to `numeric(18,6)` — może mieć ponad 15 cyfr znaczących, których `number`
 * nie utrzyma. Gdy `decimals` jest ustawione, formatujemy więc DOKŁADNIE z tekstu (wspólny
 * `formatDecimalCurrencyCell`, ta sama sonda co dla liczb zwykłych); gdy silnik nie potrafi tak
 * sformatować zapisu o tylu cyfrach, pokazujemy surową wartość zamiast zaokrąglonej. Bez `decimals`
 * zostaje dotychczasowy format (przez `Number`) — wygląd bez zapisanego układu się nie zmienia.
 */
function formatUnitPrice(
  value: string | null | undefined,
  currency: string,
  decimals?: number,
): string {
  if (value == null || value === '') return EMPTY_DASH;
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  // Kod waluty spoza ISO 4217 (patrz `formatMoney`) → wartość z surowym kodem.
  if (!isFormattableCurrencyCode(currency)) return `${value} ${currency}`;
  if (decimals !== undefined) {
    return formatDecimalCurrencyCell(value, decimals, currency) ?? value;
  }
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(n);
}

/**
 * Formatuje decimal-string jako zwykłą liczbę (ilość / procent rabatu). `decimals` z układu
 * kolumny ustala stałą liczbę miejsc po przecinku (formatowanie dokładnie z zapisu tekstowego).
 */
function formatPlainNumber(
  value: string | null | undefined,
  maxFractionDigits = 6,
  decimals?: number,
): string {
  if (value == null || value === '') return EMPTY_DASH;
  const fixed = formatDecimalCell(value, decimals);
  if (fixed !== null) return fixed;
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: maxFractionDigits }).format(n);
}

/**
 * Formatuje wartość pola dodatkowego (ext) wg jego `fieldType` — reużywa istniejące
 * formattery liczb tego pliku (`formatPlainNumber`). `t` wstrzyknięty z callsite'u
 * (etykieta boolean Tak/Nie, i18n). Pole nieznalezione (osierocony slug — patrz
 * układ kolumn: w praktyce nieosiągalne, bo `columns` i ta mapa pochodzą
 * z tej samej listy pól) → surowy `String(value)` zamiast crasha.
 *
 * `select`/`multiselect` mapują zapisany kod na `label` z `field.options` (inline,
 * bez fetchu) — mirror logiki `resolveSelectOptions` w `ExtFieldValueInput.tsx`, ale
 * bez gałęzi słownikowej: pole ze `dictionaryCode` zostaje surowe (finding [MED]
 * przeglądu frontendu — fetch słownika w odczycie to osobny dług, patrz TODO niżej).
 */
function formatExtValue(
  value: unknown,
  field: DocumentItemExtFieldDef | undefined,
  t: (key: string) => string,
  decimals?: number,
): string {
  if (value === null || value === undefined || value === '') return EMPTY_DASH;
  switch (field?.fieldType) {
    case 'integer':
      return formatPlainNumber(String(value), 0);
    case 'numeric':
      return formatPlainNumber(String(value), 6, decimals);
    case 'boolean':
      return value ? t('common.yes') : t('common.no');
    case 'date': {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('pl-PL');
    }
    case 'timestamp': {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('pl-PL');
    }
    // Pole ze `dictionaryCode` (opcje ze słownika, nie z `field.options` inline) ZOSTAJE
    // surowe tutaj — zmapowanie kodu na etykietę wymagałoby fetchu słownika w odczycie
    // (ta funkcja nie jest komponentem, nie może wołać `useDictionary`).
    // TODO(deferred, chip): etykiety słownikowe/reference w odczycie wymagają fetchu słownika.
    case 'select': {
      if (field?.dictionaryCode) return String(value);
      const options = field?.options ?? [];
      const match = options.find((option) => String(option.value) === String(value));
      return match?.label ?? String(value);
    }
    case 'multiselect': {
      if (!Array.isArray(value)) return String(value);
      if (field?.dictionaryCode) return value.map((v) => String(v)).join(', ');
      const options = field?.options ?? [];
      return value
        .map((v) => {
          const match = options.find((option) => String(option.value) === String(v));
          return match?.label ?? String(v);
        })
        .join(', ');
    }
    // 'reference' pola pozostają surowe (default poniżej) — etykieta wymagałaby fetchu
    // rekordu referencyjnego w odczycie; patrz TODO powyżej.
    default:
      return String(value);
  }
}

const th = 'px-3 py-2 text-left text-xs font-semibold text-text-secondary';
const thRight = 'px-3 py-2 text-right text-xs font-semibold text-text-secondary';
const td = 'px-3 py-2 text-text-primary';
const tdRight = 'px-3 py-2 text-right tabular-nums text-text-primary';
const baseHint = 'block text-xs font-normal text-text-tertiary';

/** Kolumny, których nagłówek dostaje sufiks `(KOD_WALUTY)` dla dokumentu walutowego. */
const CURRENCY_SUFFIXED_COLUMNS: ReadonlySet<DocumentItemColumnId> = new Set([
  'unitPriceNet',
  'valueNet',
  'valueVat',
  'valueGross',
]);

export function DocumentItemsSection({
  items,
  vatBreakdown,
  amountNet,
  amountVat,
  amountGross,
  currencyCode,
  columnSettings,
}: DocumentItemsSectionProps) {
  const { t } = useTranslation();
  const currency = currencyCode || 'PLN';
  const isForeign = currency !== 'PLN';
  const rows = items ?? [];
  const breakdown = vatBreakdown ?? [];
  // Fala B3 — definicje pól dodatkowych (ext) modułu `document_items`. Pusta lista
  // → deskryptory tylko wbudowane, zero zmiany zachowania względem stanu sprzed tej fali.
  const { descriptors, extFields: extFieldDefs } = useDocumentItemColumnsModel();
  const extFieldBySlug = useMemo(
    () => new Map(extFieldDefs.map((field) => [field.slug, field])),
    [extFieldDefs],
  );
  const readDescriptors = useMemo(
    () => documentItemsReadViewDescriptors(descriptors),
    [descriptors],
  );
  // Kolumny widoczne w kolejności układu; id spoza znanych kolumn pozycji (nie powinno się zdarzyć —
  // deskryptory są nasze) odpada po cichu jak dawniej osierocone id.
  const columns = useMemo(
    () =>
      resolveEmbeddedTableColumns(readDescriptors, columnSettings, {
        legacy: documentItemsLegacyReader,
      }).flatMap((col) => {
        const colId = asDocumentItemColumnId(col.id);
        return col.visible && colId !== undefined ? [{ colId, col }] : [];
      }),
    [readDescriptors, columnSettings],
  );
  // Sumy nagłówkowe są niezależne od `items` — dokumenty sprzed migracji 0094
  // (bez document_items) mają zapisane amount_net/amount_vat/amount_gross i
  // muszą je pokazać mimo pustej listy pozycji (finding [MED] increment 1).
  const hasTotals = amountNet != null || amountVat != null || amountGross != null;

  const suffix = (label: string) => (isForeign ? `${label} (${currency})` : label);

  // Jawna szerokość choć jednej kolumny → `table-layout: fixed` + `<colgroup>` + minimalna szerokość
  // tabeli (w wąskim kontenerze tabela rośnie i kontener się przewija, zamiast zapadać kolumny bez
  // szerokości do 0 px); bez żadnej szerokości tabela zostaje w układzie automatycznym jak dotąd.
  const tableColumns = columns.map(({ col }) => ({ id: col.id, width: col.width }));
  const fixedLayout = hasExplicitWidths(tableColumns);
  const tableMinWidth = fixedTableMinWidth(tableColumns);

  /** Klasy nagłówka kolumny: wyrównanie z układu (jawne, domyślne deskryptora albo wg rodzaju). */
  function headerClass(col: ResolvedEmbeddedTableColumn): string {
    return cn(
      'px-3 py-2 text-xs font-semibold text-text-secondary',
      columnAlignClass(effectiveColumnAlign(col)),
    );
  }

  /** Klasy komórki kolumny: wyrównanie z układu; cyfry tabelaryczne dla kolumn dziesiętnych
   * i liczbowych pól dodatkowych (jak dotąd w komórkach wyrównanych do prawej). */
  function cellClass(colId: DocumentItemColumnId, col: ResolvedEmbeddedTableColumn): string {
    const numeric =
      col.descriptor.kind === 'decimal' ||
      (col.descriptor.kind === 'integer' && isExtColumnId(colId));
    return cn(
      'px-3 py-2 text-text-primary',
      numeric && 'tabular-nums',
      columnAlignClass(effectiveColumnAlign(col)),
    );
  }

  /** Nagłówek: własna etykieta z układu albo etykieta deskryptora (wbudowana z i18n / nazwa pola
   * ext); sufiks waluty dla dokumentu walutowego dopisujemy też do własnej etykiety. */
  function renderHeaderCell(
    colId: DocumentItemColumnId,
    col: ResolvedEmbeddedTableColumn,
  ): React.ReactNode {
    const label = col.label ?? col.descriptor.label;
    return CURRENCY_SUFFIXED_COLUMNS.has(colId) ? suffix(label) : label;
  }

  function renderBodyCell(
    colId: DocumentItemColumnId,
    col: ResolvedEmbeddedTableColumn,
    item: DocumentLineItem,
  ): React.ReactNode {
    const className = cellClass(colId, col);
    // Miejsca po przecinku z układu — tylko prezentacja; brak = dotychczasowe formatowanie.
    const decimals = col.decimals;
    if (isExtColumnId(colId)) {
      const slug = extColumnSlug(colId);
      const field = extFieldBySlug.get(slug);
      return (
        <td key={colId} className={className} data-testid={`document-item-ext-${slug}-${item.id}`}>
          {formatExtValue(item.extValues?.[slug], field, t, decimals)}
        </td>
      );
    }
    switch (colId) {
      case 'lineNumber':
        return (
          <td key={colId} className={className}>
            {item.lineNumber}
          </td>
        );
      case 'name':
        return (
          <td key={colId} className={className}>
            {item.name}
            {item.productCode && (
              <span className="block text-xs text-text-tertiary">{item.productCode}</span>
            )}
          </td>
        );
      case 'quantity':
        return (
          <td key={colId} className={className}>
            {formatPlainNumber(item.quantity, 6, decimals)}
          </td>
        );
      case 'unit':
        return (
          <td key={colId} className={className}>
            {item.unit ?? EMPTY_DASH}
          </td>
        );
      case 'unitPriceNet':
        return (
          <td key={colId} className={className}>
            {formatUnitPrice(item.unitPriceNetInCurrency, currency, decimals)}
            {isForeign && (
              <span className={baseHint} data-testid="base-currency-hint">
                {formatUnitPrice(item.unitPriceNet, 'PLN', decimals)}
              </span>
            )}
          </td>
        );
      case 'discountPercent':
        return (
          <td key={colId} className={className}>
            {formatPlainNumber(item.discountPercent, 2, decimals)}
          </td>
        );
      case 'valueNet':
        return (
          <td key={colId} className={className}>
            {formatMoney(item.valueNetInCurrency, currency, decimals)}
            {isForeign && (
              <span className={baseHint} data-testid="base-currency-hint">
                {formatMoney(item.valueNet, 'PLN', decimals)}
              </span>
            )}
          </td>
        );
      case 'vatRateCode':
        return (
          <td key={colId} className={className}>
            {item.vatRateCode ?? EMPTY_DASH}
          </td>
        );
      case 'valueVat':
        return (
          <td key={colId} className={className}>
            {formatMoney(item.valueVatInCurrency, currency, decimals)}
            {isForeign && (
              <span className={baseHint} data-testid="base-currency-hint">
                {formatMoney(item.valueVat, 'PLN', decimals)}
              </span>
            )}
          </td>
        );
      case 'valueGross':
        return (
          <td key={colId} className={className}>
            {formatMoney(item.valueGrossInCurrency, currency, decimals)}
            {isForeign && (
              <span className={baseHint} data-testid="base-currency-hint">
                {formatMoney(item.valueGross, 'PLN', decimals)}
              </span>
            )}
          </td>
        );
      default:
        return null;
    }
  }

  return (
    <div className="space-y-4" data-testid="document-items-section">
      {rows.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-border-secondary bg-bg-secondary p-8 text-center"
          data-testid="document-items-empty"
        >
          <p className="text-sm text-text-tertiary">{t('tenant.documents.items.empty')}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-border-primary">
            <table
              className={fixedLayout ? 'w-full table-fixed text-sm' : 'w-full text-sm'}
              style={tableMinWidth !== undefined ? { minWidth: `${tableMinWidth}px` } : undefined}
              data-testid="document-items-table"
            >
              <TableColgroup columns={tableColumns} />
              <thead>
                <tr className="border-b border-border-primary bg-bg-secondary">
                  {columns.map(({ colId, col }) => (
                    <th key={colId} className={headerClass(col)}>
                      {renderHeaderCell(colId, col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-border-primary last:border-0 hover:bg-bg-secondary/30"
                    data-testid={`document-item-row-${item.id}`}
                  >
                    {columns.map(({ colId, col }) => renderBodyCell(colId, col, item))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {breakdown.length > 0 && (
            <div
              className="overflow-x-auto rounded border border-border-primary"
              data-testid="document-items-vat-breakdown"
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-primary bg-bg-secondary">
                    <th className={th} colSpan={4}>
                      {t('tenant.documents.items.vatBreakdown.title')}
                    </th>
                  </tr>
                  <tr className="border-b border-border-primary">
                    <th className={th}>{t('tenant.documents.items.vatBreakdown.rate')}</th>
                    <th className={thRight}>
                      {suffix(t('tenant.documents.items.vatBreakdown.net'))}
                    </th>
                    <th className={thRight}>
                      {suffix(t('tenant.documents.items.vatBreakdown.vat'))}
                    </th>
                    <th className={thRight}>
                      {suffix(t('tenant.documents.items.vatBreakdown.gross'))}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((row) => (
                    <tr
                      key={row.vatRateCode}
                      className="border-b border-border-primary last:border-0"
                      data-testid={`document-items-vat-row-${row.vatRateCode}`}
                    >
                      <td className={td}>{row.vatRateCode}</td>
                      <td className={tdRight}>
                        {formatMoney(row.valueNetInCurrency, currency)}
                        {isForeign && (
                          <span className={baseHint} data-testid="base-currency-hint">
                            {formatMoney(row.valueNet, 'PLN')}
                          </span>
                        )}
                      </td>
                      <td className={tdRight}>
                        {formatMoney(row.valueVatInCurrency, currency)}
                        {isForeign && (
                          <span className={baseHint} data-testid="base-currency-hint">
                            {formatMoney(row.valueVat, 'PLN')}
                          </span>
                        )}
                      </td>
                      <td className={tdRight}>
                        {formatMoney(row.valueGrossInCurrency, currency)}
                        {isForeign && (
                          <span className={baseHint} data-testid="base-currency-hint">
                            {formatMoney(row.valueGross, 'PLN')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {hasTotals && (
        <div
          className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 rounded border border-border-primary bg-bg-secondary px-4 py-3 text-sm"
          data-testid="document-items-totals"
        >
          <span className="text-text-secondary">
            {t('tenant.documents.items.totals.net', { currency })}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(amountNet, currency)}
            </span>
          </span>
          <span className="text-text-secondary">
            {t('tenant.documents.items.totals.vat', { currency })}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(amountVat, currency)}
            </span>
          </span>
          <span className="text-text-secondary">
            {t('tenant.documents.items.totals.gross', { currency })}:{' '}
            <span className="font-semibold tabular-nums text-text-primary">
              {formatMoney(amountGross, currency)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}
